import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { InputHTMLAttributes, ReactElement, SyntheticEvent, ReactNode } from 'react'

import InputWrapper from '../InputWrapper'

import { createUUID } from '~/utils/uuid'

import './styles.scss'

export interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  className?: string
  defaultMaxValue?: number
  defaultMinValue?: number
  label?: string
  labelPosition?: 'top' | 'left'
  max: number
  min: number
  onChange: ({ max, min }: { max: number; min: number }) => void
  tooltip?: ReactNode
}

const RangeInput = ({
  className,
  defaultMaxValue,
  defaultMinValue,
  label,
  labelPosition = 'top',
  max,
  min,
  onChange,
  tooltip,
  ...props
}: Props): ReactElement => {
  /** Local state */

  const id = createUUID()

  const [minValRef, setMinValRef] = useState(defaultMinValue ?? min)

  const [maxValRef, setMaxValRef] = useState(defaultMaxValue ?? max)

  const [minVal, setMinVal] = useState(defaultMinValue ?? min)

  const [maxVal, setMaxVal] = useState(defaultMaxValue ?? max)

  const range = useRef<HTMLInputElement>(null)

  const getPercent = useCallback((value: number) => Math.round(((value - min) / (max - min)) * 100), [min, max])

  /** Effects */

  useEffect(() => {
    if (range.current) {
      const minPercent = getPercent(minVal)
      const maxPercent = getPercent(maxValRef)

      range.current.style.left = `${minPercent}%`
      range.current.style.width = `${maxPercent - minPercent}%`
    }
  }, [maxValRef, minVal, getPercent])

  useEffect(() => {
    if (range.current) {
      const minPercent = getPercent(minValRef)
      const maxPercent = getPercent(maxVal)

      range.current.style.width = `${maxPercent - minPercent}%`
    }
  }, [maxVal, minValRef, getPercent])

  /** Handlers */

  const handleChange = (type: 'min' | 'max') => {
    return (evt: SyntheticEvent<HTMLInputElement>) => {
      if (type === 'min') {
        const value = Math.min(Number(evt.currentTarget.value), maxVal - 1)
        setMinVal(value)
        setMinValRef(value)

        onChange({ max: maxVal, min: value })
      } else if (type === 'max') {
        const value = Math.max(Number(evt.currentTarget.value), minVal + 1)
        setMaxVal(value)
        setMaxValRef(value)

        onChange({ max: value, min: minVal })
      }
    }
  }

  /** Render */

  return (
    <InputWrapper
      className={classNames('range-input-component', className, `${labelPosition}-label`)}
      disabled={props.disabled}
      inputId={id}
      label={label}
      labelPosition={labelPosition}
      required={props.required}
      size="small"
      tooltip={tooltip}
    >
      <input
        className="range-input thumb thumb--left"
        disabled={props.disabled}
        max={max}
        min={min}
        onChange={handleChange('min')}
        style={{ zIndex: minVal > max - 100 ? '5' : undefined }}
        type="range"
        value={minVal}
      />
      <input
        className="range-input thumb thumb--right"
        disabled={props.disabled}
        max={max}
        min={min}
        onChange={handleChange('max')}
        type="range"
        value={maxVal}
      />
      <div className="slider">
        <div className="slider__left-value">{minVal}</div>
        <div className="slider__right-value">{maxVal}</div>
        <div className="slider__track" />
        <div className="slider__range" ref={range} />
      </div>
    </InputWrapper>
  )
}

export default RangeInput
